/* Niagara v14-69: prefill the existing Shopify cart; never rebuild it. */
(() => {
  'use strict';
  const script = document.querySelector('script[data-njt-checkout-preference]');
  if (!script || window.njtCheckoutPreferenceLoaded) return;
  window.njtCheckoutPreferenceLoaded = true;
  const root = window.Shopify?.routes?.root || '/';
  const pickupHandle = script.dataset.pickupHandle;
  const query = `mutation DeliveryPreference($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { id buyerIdentity { preferences { delivery { deliveryMethod pickupHandle } } } }
      userErrors { field message }
    }
  }`;
  let busy = false;
  let cartRevision = 0;
  document.addEventListener('change', event => {
    if (event.target.closest('cart-items, cart-drawer-items')) cartRevision++;
  }, true);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => { cartRevision++; });
  }
  const allowed = new WeakSet();

  async function jsonFetch(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { ...options, credentials: 'same-origin', signal: controller.signal,
        headers: { Accept: 'application/json', ...options.headers } });
      if (!response.ok) throw new Error('Request failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  function methodFor(cart) {
    const methods = new Set(cart.items.filter(item => item.requires_shipping !== false).map(item =>
      item.properties?.['Delivery preference'] === 'Niagara Pickup' ? 'PICK_UP' : 'SHIPPING'));
    return methods.size > 1 ? 'MIXED' : [...methods][0] || null;
  }

  function choose(title, message, choices) {
    return new Promise(resolve => {
      const previous = document.activeElement;
      const dialog = document.createElement('dialog');
      dialog.className = 'njt-checkout-choice';
      dialog.style.cssText = 'border:0;border-radius:12px;padding:24px;max-width:440px;width:calc(100% - 32px);color:#171717;background:#fff;';
      const heading = document.createElement('h2');
      heading.id = 'njt-checkout-choice-title';
      heading.textContent = title;
      heading.style.cssText = 'font-size:22px;margin:0 0 12px;';
      const text = document.createElement('p');
      text.textContent = message;
      text.style.cssText = 'font-size:15px;line-height:1.5;margin:0 0 20px;';
      const buttons = document.createElement('div');
      buttons.style.cssText = 'display:grid;gap:10px;';
      choices.forEach(([value, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--primary';
        button.textContent = label;
        button.addEventListener('click', () => dialog.close(value));
        buttons.append(button);
      });
      dialog.setAttribute('aria-labelledby', heading.id);
      dialog.append(heading, text, buttons);
      dialog.addEventListener('close', () => {
        const value = dialog.returnValue;
        dialog.remove();
        previous?.focus();
        resolve(value);
      }, { once: true });
      document.body.append(dialog);
      dialog.showModal();
    });
  }

  async function setPreference(cart, method) {
    if (!cart.token || !cart.token.includes('?key=')) throw new Error('Cart key unavailable');
    if (method === 'PICK_UP' && !/^\d+$/.test(pickupHandle || '')) throw new Error('Pickup location unavailable');
    const cartId = cart.token.startsWith('gid://shopify/Cart/') ? cart.token : `gid://shopify/Cart/${cart.token}`;
    const data = await jsonFetch('/api/2026-10/graphql.json', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: { cartId, buyerIdentity: { preferences: { delivery: {
        deliveryMethod: [method], pickupHandle: method === 'PICK_UP' ? [pickupHandle] : []
      } } } } })
    });
    const result = data.data?.cartBuyerIdentityUpdate;
    if (data.errors?.length || result?.userErrors?.length ||
        result?.cart?.buyerIdentity?.preferences?.delivery?.deliveryMethod?.[0] !== method) {
      throw new Error('Preference not confirmed');
    }
  }

  document.addEventListener('submit', async event => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || !['cart', 'CartDrawer-Form'].includes(form.id) || submitter?.name !== 'checkout') return;
    if (allowed.has(form)) { allowed.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    submitter.setAttribute('aria-busy', 'true');
    let proceed = false;
    try {
      // Let the theme's debounced quantity/note updates settle before reading the cart.
      await new Promise(resolve => setTimeout(resolve, 400));
      if (document.querySelector('cart-items.cart__items--disabled, cart-drawer-items.cart__items--disabled')) {
        await choose('Cart is updating', 'Please wait for the quantity update to finish, then try checkout again.', [['cancel', 'Back to cart']]);
        return;
      }
      const cart = await jsonFetch(`${root}cart.js`, { cache: 'no-store' });
      if (!cart.item_count) return;
      const revision = cartRevision;
      let method = methodFor(cart);
      if (method === 'MIXED') {
        method = await choose('How would you like your order?',
          'Your cart has both shipping and pickup preferences. Choose a preference for this order. Available options and final availability will be confirmed at checkout.',
          [['SHIPPING', 'Ship to Home'], ['PICK_UP', 'Local Pickup'], ['cancel', 'Back to cart']]);
        if (!['SHIPPING', 'PICK_UP'].includes(method)) return;
      }
      if (method) await setPreference(cart, method);
      if (revision !== cartRevision) {
        await choose('Your cart changed', 'Please review your updated cart and press Checkout again.', [['cancel', 'Back to cart']]);
        return;
      }
      proceed = true;
    } catch (_) {
      // Keep checkout usable without silently claiming the preference was applied.
      const action = await choose('Confirm delivery at checkout',
        'We could not carry your delivery preference through. You can continue and select Ship or Pickup on the next page, or return to your cart and try again.',
        [['continue', 'Continue to checkout'], ['cancel', 'Back to cart']]);
      proceed = action === 'continue';
    } finally {
      busy = false;
      submitter.removeAttribute('aria-busy');
    }
    if (proceed) {
      // Preserve the theme's native form submission, notes, quantities and checkout handling.
      const currentForm = document.getElementById(form.id);
      const currentSubmitter = document.getElementById(submitter.id);
      if (currentForm && currentSubmitter && !currentSubmitter.disabled) {
        allowed.add(currentForm);
        currentForm.requestSubmit(currentSubmitter);
      }
    }
  }, true);
})();
